import '../sim/ai/brains';
import { type ClassDef, registry } from '../sim/registry';
import type { ZoneLayout } from '../zones/types';
import { ENDGAME_BOSSES, registerEndgameBossScripts } from './bosses';
import { ARSENALIST } from './classes/arsenalist';
import { CANTOR } from './classes/cantor';
import { FIENDBINDER } from './classes/fiendbinder';
import { RAVAGER } from './classes/ravager';
import { STORMWEAVER } from './classes/stormweaver';
import { WINDFIST } from './classes/windfist';
import { ALL_MONSTERS, registerAllBossScripts } from './monsters';
import { OVERWORLD_ZONES } from './zones';
import { INSTANCE_ZONES } from './zones/instances';

/** Playable classes in character-select order. */
export const CLASSES: readonly ClassDef[] = [RAVAGER, STORMWEAVER, ARSENALIST, WINDFIST, CANTOR, FIENDBINDER];

/** Zone layouts are built lazily and cached (deterministic per id). */
const zoneBuilders = new Map<string, () => ZoneLayout>();
const zoneCache = new Map<string, ZoneLayout>();

export function registerZone(id: string, build: () => ZoneLayout): void {
  zoneBuilders.set(id, build);
}

export function getZone(id: string): ZoneLayout {
  let z = zoneCache.get(id);
  if (!z) {
    const b = zoneBuilders.get(id);
    if (!b) throw new Error(`Unknown zone '${id}'`);
    z = b();
    zoneCache.set(id, z);
  }
  return z;
}

export function hasZone(id: string): boolean {
  return zoneBuilders.has(id);
}

let registered = false;

/** Registers every class, monster and zone. Safe to call more than once. */
export function registerContent(): void {
  if (registered) return;
  registered = true;
  for (const c of CLASSES) registry.registerClass(c);
  for (const m of [...ALL_MONSTERS, ...ENDGAME_BOSSES]) registry.registerMonster(m);
  registerAllBossScripts();
  registerEndgameBossScripts();
  for (const [id, build] of Object.entries({ ...OVERWORLD_ZONES, ...INSTANCE_ZONES })) registerZone(id, build);
}
